import React, { useEffect } from "react";
import clsx from "clsx";
import { Dialog, Transition } from "@headlessui/react";
import { useLocation, Link } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useState } from "react";
import LogoutModal from "./LogoutModal";
import { getUserRoles } from "../shared/menuService";

const UserRoles = getUserRoles();

const navigation = [
  {
    name: "Trips",
    href: "/",
    roles: [UserRoles.ADMIN, UserRoles.CALLCENTER_REP, UserRoles.TRANSPORT_COORDINATOR],
    icon: (props) => (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" {...props}>
        <path
          fill="currentColor"
          d="M9 21q-1.65 0-2.825-1.175T5 17V8.825Q4.125 8.5 3.562 7.738T3 6q0-1.25.875-2.125T6 3q1.25 0 2.125.875T9 6q0 .975-.563 1.738T7 8.825V17q0 .825.588 1.413T9 19q.825 0 1.413-.588T11 17V7q0-1.65 1.175-2.825T15 3q1.65 0 2.825 1.175T19 7v8.175q.875.325 1.438 1.088T21 18q0 1.25-.875 2.125T18 21q-1.25 0-2.125-.875T15 18q0-.975.563-1.75T17 15.175V7q0-.825-.588-1.413T15 5q-.825 0-1.413.588T13 7v10q0 1.65-1.175 2.825T9 21ZM6 7q.425 0 .713-.288T7 6q0-.425-.288-.713T6 5q-.425 0-.713.288T5 6q0 .425.288.713T6 7Zm12 12q.425 0 .713-.288T19 18q0-.425-.288-.713T18 17q-.425 0-.713.288T17 18q0 .425.288.713T18 19ZM6 6Zm12 12Z"
        />
      </svg>
    ),
    exact: true,
  },
  {
    name: "Bucket",
    href: "/bucket",
    roles: [UserRoles.ADMIN, UserRoles.CALLCENTER_REP, UserRoles.TRANSPORT_COORDINATOR],
    icon: (props) => (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
      </svg>
    ),
  },
  {
    name: "Maps",
    href: "/maps",
    roles: [UserRoles.ADMIN, UserRoles.CALLCENTER_REP, UserRoles.TRANSPORT_COORDINATOR],
    icon: (props) => (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
      </svg>
    ),
  },
  {
    name: "Portal",
    href: "/portal-plan",
    roles: [UserRoles.ADMIN, UserRoles.CALLCENTER_REP, UserRoles.TRANSPORT_COORDINATOR],
    icon: (props) => (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
      </svg>
    ),
  },
  {
    name: "Analytics",
    href: "/charts",
    roles: [UserRoles.ADMIN, UserRoles.CALLCENTER_REP, UserRoles.TRANSPORT_COORDINATOR],
    icon: (props) => (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
  },
];

export default function SideNav() {
  const { pathname } = useLocation();
  const { user, sidebarOpen, setSidebarOpen, setLogoutModalOpen } = useAuth();

  const handleLogoutModal = () => {
    setLogoutModalOpen(true);
  };

  useEffect(() => {
  }, [user]);

  const canView = (roles) => {
    if (!user) return false;

    if (user.roles && user.roles.includes('cooperate')) {
      return true;
    }

    if (!user.roles) return true;

    let result;
    result = roles.filter(r => user.roles.includes(r));

    return (result && result.length) ? true : false;
  }

  const isActive = (item) => {
    if (item.exact) return pathname === item.href;
    return pathname === item.href || pathname.startsWith(item.href + '/');
  };

  return (
    <>
      <div className="hidden h-full w-28 overflow-y-auto bg-nav-dark lg:block border-r border-nav-base">
        <div className="flex w-full flex-col items-center py-4 h-full">
          <div className="flex flex-col justify-center text-center flex-shrink-0 items-center border-b pb-4 border-white/10 w-full">
            <img
              className="h-10 w-10 rounded-full object-cover"
              src="https://provider.nemtbusiness.com/assets/images/branding/N-Logo.png"
              alt="Logo"
            />
          </div>
          <div className="mt-4 w-full flex-1 px-2 flex flex-col justify-between h-full">
            <div className="space-y-1">
              {navigation.map((item, index) => (
                canView(item.roles) &&
                <Link
                  key={index}
                  to={item.href}
                  className={clsx(
                    isActive(item)
                      ? "bg-white/10 text-nav-light"
                      : "text-nav-dark hover:bg-white/10 hover:text-nav-light",
                    "group w-full p-3 rounded-md flex flex-col items-center text-xs font-medium cursor-pointer no-underline"
                  )}
                >
                  <item.icon
                    className={clsx(
                      isActive(item)
                        ? "text-nav-svg-light"
                        : "text-nav-svg-dark group-hover:text-nav-light",
                      " h-6 w-6"
                    )}
                    aria-hidden="true"
                  />
                  <span className="mt-2">{item.name}</span>
                </Link>
              ))}
            </div>
            <div className="space-y-1">
              <div
                className="group w-full py-3 px-1 rounded-md flex flex-col items-center justify-center text-center text-xs font-medium text-nav-dark"
              >
                {user && user.photoURL ? (
                  <div className="h-10 w-10 rounded-full overflow-hidden ">
                    <img
                      className="h-full w-full object-cover"
                      src={user.photoURL}
                      alt={user.displayName || user.email}
                    />
                  </div>
                ) : (
                  <>
                    <svg
                      className="h-10 w-10 rounded-full text-main-base"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18.685 19.097A9.723 9.723 0 0021.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 003.065 7.097A9.716 9.716 0 0012 21.75a9.716 9.716 0 006.685-2.653zm-12.54-1.285A7.486 7.486 0 0112 15a7.486 7.486 0 015.855 2.812A8.224 8.224 0 0112 20.25a8.224 8.224 0 01-5.855-2.438zM15.75 9a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </>
                )}
                <span className="mt-2 w-full truncate px-1" title={user ? (user.displayName || user.email) : ""}>
                  {user ? <>{user.displayName || user.email}</> : <>Loading...</>}
                </span>
              </div>
              <div
                onClick={handleLogoutModal}
                className="group w-full p-3 rounded-md flex flex-col items-center justify-center text-center text-xs font-medium cursor-pointer text-nav-dark hover:bg-nav-light hover:text-nav-light"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-6 h-6 text-nav-svg-dark group-hover:text-nav-light"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9"
                  />
                </svg>

                <span className="mt-2">Logout</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 lg:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-main-dark/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-nav-dark px-6 py-4">
                  <div className="flex flex-col justify-center text-center flex-shrink-0 items-center border-b pb-5 border-white/10 w-full">
                    <img
                      className="h-10 w-10 rounded-full object-cover"
                      src="https://provider.nemtbusiness.com/assets/images/branding/N-Logo.png"
                      alt="Logo"
                    />
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul role="list" className="-mx-2 space-y-1">
                          {navigation.map((item) => (
                            <li key={item.name}>
                              <Link
                                onClick={() => setSidebarOpen(false)}
                                to={item.href}
                                className={clsx(
                                  isActive(item)
                                    ? "bg-white/10 text-nav-light"
                                    : "text-nav-dark hover:bg-white/10 hover:text-nav-light",
                                  "group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold cursor-pointer no-underline"
                                )}
                              >
                                <item.icon
                                  className={clsx(
                                    isActive(item)
                                      ? "text-nav-svg-light"
                                      : "text-nav-svg-dark group-hover:text-nav-light",
                                    " h-6 w-6"
                                  )}
                                  aria-hidden="true"
                                />
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
      <LogoutModal />
    </>
  );
}
